import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, SUPERADMIN_ROUTES } from '@dukene/shared';
import {
  LayoutDashboard,
  Building2,
  BarChart3,
  Settings,
  LogOut,
  ChevronRight,
  DollarSign,
  CreditCard,
  UserPlus,
  MessageSquare,
  Truck,
  Mail,
  Route,
  Activity,
} from 'lucide-react';

const navItems = [
  { label: 'Dashboard', path: SUPERADMIN_ROUTES.DASHBOARD, icon: LayoutDashboard },
  { label: 'Organisations', path: SUPERADMIN_ROUTES.ORGANISATIONS, icon: Building2 },
  { label: 'Affiliates', path: SUPERADMIN_ROUTES.AFFILIATES, icon: UserPlus },
  { label: 'Suppliers', path: SUPERADMIN_ROUTES.SUPPLIERS, icon: Truck },
  { label: 'WhatsApp', path: SUPERADMIN_ROUTES.WHATSAPP, icon: MessageSquare },
  { label: 'Shipping', path: SUPERADMIN_ROUTES.SHIPPING, icon: Truck },
  { label: 'Order Routes', path: SUPERADMIN_ROUTES.ORDER_ROUTES, icon: Route },
  { label: 'Analytics', path: SUPERADMIN_ROUTES.ANALYTICS, icon: BarChart3 },
  { label: 'Metrics', path: SUPERADMIN_ROUTES.METRICS, icon: Activity },
  { label: 'Payments', path: SUPERADMIN_ROUTES.PAYMENTS, icon: CreditCard },
  { label: 'Revenue', path: SUPERADMIN_ROUTES.REVENUE, icon: DollarSign },
  { label: 'Emails', path: SUPERADMIN_ROUTES.EMAIL, icon: Mail },
  { label: 'Settings', path: SUPERADMIN_ROUTES.SETTINGS, icon: Settings },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar - Modern White */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-white border-r border-gray-200">
        {/* Logo/Brand */}
        <div className="flex h-14 items-center gap-3 border-b border-gray-200 px-6">
          <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
            <img src="/favicon.png" alt="Dukene Logo" className="w-full h-full object-contain p-0.5" />
          </div>
          <div>
            <h1 className="text-sm font-black text-black tracking-tight">DUKENE</h1>
            <p className="text-[9px] text-gray-500 uppercase tracking-wider">Platform</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (item.path !== SUPERADMIN_ROUTES.DASHBOARD && location.pathname.startsWith(item.path));

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all ${isActive
                  ? 'bg-black text-white shadow-lg'
                  : 'text-gray-600 hover:text-black hover:bg-gray-50'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </div>
                {isActive && <ChevronRight className="w-3.5 h-3.5" />}
              </Link>
            );
          })}
        </nav>

        {/* User Section at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <Link
            to={SUPERADMIN_ROUTES.PROFILE}
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center group-hover:scale-105 transition-transform">
              <span className="text-xs font-bold text-white">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-black truncate">
                {user ? `${user.firstName} ${user.lastName}` : 'Admin'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'admin@dukene.com'}
              </p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-black hover:bg-gray-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="pl-64">
        {/* Header - Minimal White */}
        <header className="sticky top-0 z-30 h-16 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="flex h-full items-center justify-between px-8">
            <div className="flex items-center gap-4">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                SuperAdmin Portal
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="px-3 py-1.5 rounded-full bg-black text-white text-xs font-medium">
                ADMIN
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-[calc(100vh-4rem)] bg-gray-50 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
